import {
  TOpsClassDetailEx,
  TOpsClassUpsertEx,
} from '../../../requests/opsRegistrationsExtra';
import { inputClass } from '../ui';

export const STATUSES = ['Available', 'Cancelled', 'Rescheduled', 'Completed'];
export const TYPES = ['Public Class', 'Private Class'];
export const LOCATIONS = ['Live Online Training', 'Onsite'];
export const TIME_ZONES = ['PST', 'EST'];
export const PROVIDERS = ['Learn iT!', 'Client / Third-Party'];

export const fieldClass = `w-full ${inputClass}`;

/** SF datetime string → value for <input type="datetime-local"> (browser-local). */
export function toLocalInput(sf: string | null): string {
  if (!sf) return '';
  const d = new Date(sf);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

export type TInstructorEval = {
  id: string;
  instructor: string | null;
  date: string | null;
  prepEase: string | null;
  advice: string | null;
  comments: string | null;
};

export const EMPTY_FORM = {
  name: '',
  classType: 'Public Class',
  status: 'Available',
  start: '',
  end: '',
  timeZone: 'PST',
  location: 'Live Online Training',
  locationAddress: '',
  videoProvider: 'Learn iT!',
  productId: '',
  instructorName: '',
  seats: '',
  cost: '',
  clientConfirmed: false,
  notifyLearners: false,
  trainerNotes: '',
  paymentNotes: '',
  learnerLink: '',
  privateClassLink: '',
  studentSurvey: '',
  instructorSurvey: '',
  courseOutline: '',
  // Delivery details (see src/ops/classes/delivery-fields.ts for which
  // of these Salesforce carries and which are the portal's own)
  prepCallStatus: '',
  customisationHours: '',
  customisationNotes: '',
  materialsStatus: '',
  materialsNote: '',
  travelStatus: '',
  travelNotes: '',
  travelPayee: '',
  invoiceCustomerForTravel: false,
  confirmOutline: false,
  confirmProjectNotes: false,
  productMatchesOutline: false,
  // staff feedback 2026-09-25
  reasonForSession: '',
  audienceInformation: '',
  recordingDetails: '',
  instructorRequirement: '',
  requestedInstructor: '',
  onsiteContact: '',
  paymentType: '',
  paymentTerms: '',
  prepaidOpportunityId: '',
};

export type TClassForm = typeof EMPTY_FORM;
export type TSetField = (
  key: keyof TClassForm,
) => (value: string | boolean) => void;

/** A saved class → the form (notifyLearners always starts unticked). */
export function formFromClass(existing: TOpsClassDetailEx): TClassForm {
  return {
    name: existing.name ?? '',
    classType: existing.classType ?? 'Public Class',
    status: existing.status ?? 'Available',
    start: toLocalInput(existing.start),
    end: toLocalInput(existing.end),
    timeZone: existing.timeZone ?? 'PST',
    location: existing.location ?? 'Live Online Training',
    locationAddress: existing.locationAddress ?? '',
    videoProvider: existing.videoProvider ?? 'Learn iT!',
    productId: existing.productId ?? '',
    instructorName: existing.instructorName ?? '',
    seats: existing.seats != null ? String(existing.seats) : '',
    cost: existing.cost != null ? String(existing.cost) : '',
    clientConfirmed: existing.clientConfirmed,
    notifyLearners: false,
    trainerNotes: existing.trainerNotes ?? '',
    paymentNotes: existing.paymentNotes ?? '',
    learnerLink: existing.links?.learnerLink ?? '',
    privateClassLink: existing.links?.privateClassLink ?? '',
    studentSurvey: existing.links?.studentSurvey ?? '',
    instructorSurvey: existing.links?.instructorSurvey ?? '',
    courseOutline: existing.links?.courseOutline ?? '',
    // Prep call: the portal's own value if set, else the Opportunity's
    // folded onto our list — either way, what the form shows is what
    // the queue and the trainer view use.
    prepCallStatus: existing.delivery?.prepCallStatus ?? '',
    customisationHours:
      existing.delivery?.customisationHours != null
        ? String(existing.delivery.customisationHours)
        : '',
    customisationNotes: existing.delivery?.customisationNotes ?? '',
    materialsStatus: existing.delivery?.materials?.status ?? '',
    materialsNote: existing.delivery?.materials?.trackingNote ?? '',
    travelStatus: existing.delivery?.travelStatus ?? '',
    travelNotes: existing.delivery?.travelNotes ?? '',
    travelPayee: existing.delivery?.travelPayee ?? '',
    invoiceCustomerForTravel: Boolean(
      existing.delivery?.invoiceCustomerForTravel,
    ),
    confirmOutline: Boolean(existing.delivery?.confirmOutline),
    confirmProjectNotes: Boolean(existing.delivery?.confirmProjectNotes),
    productMatchesOutline: Boolean(existing.delivery?.productMatchesOutline),
    reasonForSession: existing.reasonForSession ?? '',
    audienceInformation: existing.audienceInformation ?? '',
    recordingDetails: existing.recordingDetails ?? '',
    instructorRequirement: existing.instructorRequirement ?? '',
    requestedInstructor: existing.requestedInstructor ?? '',
    onsiteContact: existing.onsiteContact ?? '',
    paymentType:
      existing.paymentTypeSource === 'portal' ? existing.paymentType ?? '' : '',
    paymentTerms: existing.paymentTerms ?? '',
    prepaidOpportunityId: existing.prepaidOpportunityId ?? '',
  };
}

/** The form → the create/update payload. */
export function formToDto(
  form: TClassForm,
  accountId: string,
  cancelling: boolean,
): TOpsClassUpsertEx {
  return {
    name: form.name.trim(),
    classType: form.classType,
    status: form.status,
    start: new Date(form.start).toISOString(),
    end: new Date(form.end).toISOString(),
    timeZone: form.timeZone || undefined,
    location: form.location || undefined,
    locationAddress: form.locationAddress || undefined,
    videoProvider: form.videoProvider || undefined,
    productId: form.productId || undefined,
    accountId: accountId || undefined,
    instructorName: form.instructorName || undefined,
    seats: form.seats === '' ? undefined : Number(form.seats),
    cost: form.cost === '' ? undefined : Number(form.cost),
    clientConfirmed: form.clientConfirmed,
    notifyLearners: cancelling ? form.notifyLearners : undefined,
    trainerNotes: form.trainerNotes,
    paymentNotes: form.paymentNotes,
    learnerLink: form.learnerLink.trim(),
    privateClassLink: form.privateClassLink.trim(),
    studentSurvey: form.studentSurvey.trim(),
    instructorSurvey: form.instructorSurvey.trim(),
    courseOutline: form.courseOutline.trim(),
    // Delivery details — '' clears a text or select, null clears hours
    prepCallStatus: form.prepCallStatus,
    customisationHours:
      form.customisationHours === '' ? null : Number(form.customisationHours),
    customisationNotes: form.customisationNotes,
    materialsStatus: form.materialsStatus,
    materialsNote: form.materialsNote,
    travelStatus: form.travelStatus,
    travelNotes: form.travelNotes,
    travelPayee: form.travelPayee,
    invoiceCustomerForTravel: form.invoiceCustomerForTravel,
    confirmOutline: form.confirmOutline,
    confirmProjectNotes: form.confirmProjectNotes,
    productMatchesOutline: form.productMatchesOutline,
    reasonForSession: form.reasonForSession,
    audienceInformation: form.audienceInformation,
    recordingDetails: form.recordingDetails,
    instructorRequirement: form.instructorRequirement,
    requestedInstructor: form.requestedInstructor,
    onsiteContact: form.onsiteContact,
    paymentType: form.paymentType,
    paymentTerms: form.paymentTerms,
    prepaidOpportunityId: form.prepaidOpportunityId,
  };
}
